import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import type {
    Feature,
    GeoJSON,
    GeoJsonProperties,
    Geometry,
    MultiPolygon as GeoJsonMultiPolygon,
    Polygon as GeoJsonPolygon,
    Position,
} from 'geojson';
import type { Source } from 'maplibre-gl';
import { MapGeoJSONFeature, MapMouseEvent, PointLike } from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';

import { MapBaseSource, MapLayerSource } from '@connect-services/client/components/map/map.component';
import { GeomLayer } from '@connect-services/client/models/geom-layer.model';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import {
    Layer,
    LayerControllerGetEntities200ResponseAllOfItemsInner as Entity,
    LayersService,
    LayerWithAttributes,
    MapboxStyle,
    MultiPolygon,
    Polygon,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

type SelectedGeometry = GeoJsonPolygon | GeoJsonMultiPolygon;
type SelectedGeometries = Record<string, SelectedGeometry>;

@Injectable({
    providedIn: 'root',
})
export class SelectZoneLayerService extends MapBaseService {
    public readonly selectedEntityUuidsBySourceId: WritableSignal<Record<string, string[]> | undefined> =
        signal(undefined);
    public selectedGeometries: SelectedGeometries = {};
    public readonly selectedSources: WritableSignal<MapBaseSource<Layer>[]> = signal([]);

    private readonly clickEventListener: (mapClickEvent: MapMouseEvent) => void;
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly layersService = inject(LayersService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly shouldDrawTableLayer = computed(() => {
        return (
            this.createFieldInterventionService.activeLayers().length ||
            this.entitiesTableByLayerService.activeLayers().length ||
            this.geographicFilterService.selectedExtentLayersAndEntities()?.layers.length
        );
    });
    private readonly snackBarService = inject(SnackBarService);
    private sourcesService = inject(SourcesService);

    public constructor() {
        super();

        this.clickEventListener = (mapClickEvent: MapMouseEvent): Promise<void> =>
            this.selectZoneLayerOnClick(mapClickEvent);
    }

    public async addSelectedLayerEntities(layerIds: string[]): Promise<void> {
        if (this.selectedSources().length) {
            await this.removeLayers();
        }

        await this.setSelectedSources(layerIds);

        const selectedEntityUuidsBySourceId: Record<string, string[]> = {};
        for (const selectedSource of this.selectedSources()) {
            selectedEntityUuidsBySourceId[selectedSource.sourceId] = [];
        }

        this.selectedEntityUuidsBySourceId.set(selectedEntityUuidsBySourceId);

        this.geographicFilterService.selectedExtentLayersAndEntities.set({
            entities: [],
            layers: layerIds,
        });
        this.addClickEvent();
    }

    public getSelectedMultiPolygon(): MultiPolygon | null {
        const selectedGeometries: (GeoJsonPolygon | GeoJsonMultiPolygon)[] = Object.values(this.selectedGeometries);

        if (!selectedGeometries.length) {
            return null;
        }

        const coordinates: Position[][][] = selectedGeometries.reduce<GeoJsonMultiPolygon['coordinates']>(
            (acc: Position[][][], geometry: SelectedGeometry) => {
                if (geometry.type === Polygon.TypeEnum.POLYGON) {
                    acc.push(geometry.coordinates);

                    return acc;
                }

                if (geometry.type === MultiPolygon.TypeEnum.MULTI_POLYGON) {
                    acc.push(...geometry.coordinates);
                }

                return acc;
            },
            [],
        );

        if (!coordinates.length) {
            return null;
        }

        return {
            coordinates,
            type: MultiPolygon.TypeEnum.MULTI_POLYGON,
        };
    }

    public async removeLayers(): Promise<void> {
        if (!this.map || !this.selectedSources().length) {
            return;
        }

        this.map.off('click', this.clickEventListener);

        const currentSources = [
            ...this.createFieldInterventionService.currentSources(),
            ...this.entitiesTableByLayerService.currentSources(),
        ];

        if (
            this.createFieldInterventionService.isCreateFieldIntervention() ||
            this.entitiesTableByLayerService.isFieldInterventionSelectionActive()
        ) {
            this.sourcesService.sources.set(currentSources);
        }

        for (const source of this.selectedSources()) {
            if (!this.map.getSource(source.sourceId)) {
                continue;
            }

            this.mapHighlightEntityService.resetHighlight(source, MapboxStyle.TypeEnum.FILL);

            if (
                this.createFieldInterventionService.activeLayers().length ||
                this.entitiesTableByLayerService.activeLayers().length
            ) {
                this.map.removeLayer(source.sourceId);

                for (const style of source.style) {
                    this.map.removeLayer(`${source.sourceId}-${style.id}`);
                }

                this.map.removeSource(source.sourceId);
            }
        }

        this.selectedGeometries = {};
        this.selectedSources.set([]);
        this.selectedEntityUuidsBySourceId.set(undefined);

        if (this.createFieldInterventionService.isCreateFieldIntervention()) {
            this.createFieldInterventionService.clearFieldInterventionSelectionFilter();

            return;
        }

        if (this.entitiesTableByLayerService.isFieldInterventionSelectionActive()) {
            await this.entitiesTableByLayerService.deleteZoneFilter();

            return;
        }
    }

    public resetState(): void {
        if (this.map) {
            this.map.off('click', this.clickEventListener);
        }

        this.selectedGeometries = {};
        this.selectedSources.set([]);
        this.selectedEntityUuidsBySourceId.set(undefined);

        this.geographicFilterService.selectedExtentLayersAndEntities.set({
            entities: [],
            layers: [],
        });
    }

    public restoreSelectedGeometriesOnMap(): void {
        if (!this.map) {
            return;
        }

        for (const source of this.selectedSources()) {
            if (!this.selectedEntityUuidsBySourceId()![source.sourceId]) {
                continue;
            }

            this.mapHighlightEntityService.manageHighlightFeature(
                source,
                this.selectedEntityUuidsBySourceId()![source.sourceId],
                LayerStyleTypeEnum.FILL,
            );
        }
    }

    public async selectAllPolygonEntitiesFromSelectedSources(): Promise<void> {
        if (!this.map || !this.selectedSources().length) {
            return;
        }

        const selectedEntityUuidsBySourceId: Record<string, string[]> = this.selectedEntityUuidsBySourceId() ?? {};

        await Promise.all(
            this.selectedSources().map((source: MapBaseSource<Layer>) => {
                if (!selectedEntityUuidsBySourceId[source.sourceId]) {
                    selectedEntityUuidsBySourceId[source.sourceId] = [];
                }

                return this.loadPolygonsFromSource(source);
            }),
        );

        this.selectedEntityUuidsBySourceId.set(selectedEntityUuidsBySourceId);
        this.highlightSelectedEntities();
    }

    private addClickEvent(): void {
        if (!this.map) {
            return;
        }

        this.map.on('click', this.clickEventListener);
    }

    private getFeatureUuid(feature: GeoJSON.Feature): string | undefined {
        const properties: GeoJsonProperties = feature.properties;

        if (properties) {
            try {
                AssertService.hasKey(properties, 'uuid');
                const uuidCandidate = properties['uuid'];

                AssertService.isString(uuidCandidate);

                return uuidCandidate;
            } catch {
                /* Ignore */
            }
        }

        if (typeof feature.id === 'string') {
            return feature.id;
        }

        return undefined;
    }

    private getLayerEntity(layerId: string, entityId: string): Promise<Entity> {
        return firstValueFrom(
            this.layersService.layerControllerGetLayersEntity({
                entityId: entityId,
                id: layerId,
            }),
        );
    }

    private highlightSelectedEntities(): void {
        for (const selectedSource of this.selectedSources()) {
            if (!this.selectedEntityUuidsBySourceId()![selectedSource.sourceId].length) {
                this.mapHighlightEntityService.resetHighlight(selectedSource, MapboxStyle.TypeEnum.FILL);
                continue;
            }

            this.mapHighlightEntityService.manageHighlightFeature(
                selectedSource,
                this.selectedEntityUuidsBySourceId()![selectedSource.sourceId],
                LayerStyleTypeEnum.FILL,
            );
        }
    }

    private isSelectableGeometry(geometry: GeoJSON.Geometry | null | undefined): geometry is SelectedGeometry {
        if (!geometry) {
            return false;
        }

        return geometry.type === Polygon.TypeEnum.POLYGON || geometry.type === MultiPolygon.TypeEnum.MULTI_POLYGON;
    }

    private async loadPolygonsFromSource(source: MapBaseSource<Layer>): Promise<void> {
        if (!this.map) {
            return;
        }

        const mapSource: Source | undefined = this.map!.getSource(source.sourceId);

        if (!AssertService.isGeoJSONSource(mapSource)) {
            return;
        }

        const data: GeoJSON = await mapSource.getData();
        AssertService.assertFeatureCollectionType(data);

        data.features.forEach((feature: Feature<Geometry, GeoJsonProperties>): void => {
            if (feature?.type !== 'Feature') {
                return;
            }

            if (!this.isSelectableGeometry(feature.geometry)) {
                return;
            }

            const uuid: string | undefined = this.getFeatureUuid(feature);
            if (!uuid) {
                return;
            }

            this.selectedGeometries[uuid] = feature.geometry;
        });
    }

    private async selectZoneLayerOnClick(mapClickEvent: MapMouseEvent): Promise<void> {
        if (this.geographicFilterService.isStatisticFilterApplied()) {
            return;
        }

        const bbox: [PointLike, PointLike] = [
            [mapClickEvent.point.x, mapClickEvent.point.y],
            [mapClickEvent.point.x, mapClickEvent.point.y],
        ];

        if (!this.map) {
            return;
        }

        const features: MapGeoJSONFeature[] = this.map.queryRenderedFeatures(bbox);
        const sourceIds = this.selectedSources().map((source) => source.sourceId);
        const filteredFeatures = features.filter(
            (feature) =>
                feature.geometry &&
                (feature.geometry.type === Polygon.TypeEnum.POLYGON ||
                    feature.geometry.type === MultiPolygon.TypeEnum.MULTI_POLYGON) &&
                sourceIds.includes(feature.source),
        );

        if (!filteredFeatures.length) {
            this.setSelectedLayersWithoutEntities();

            return;
        }

        if (!this.selectedSources().length) {
            return;
        }

        await this.setSelectedEntityUuids(filteredFeatures);

        this.highlightSelectedEntities();
    }

    private async setSelectedEntityUuids(filteredFeatures: MapGeoJSONFeature[]): Promise<void> {
        const bySourceId = this.selectedEntityUuidsBySourceId() ?? {};

        for (const feature of filteredFeatures) {
            const source = this.selectedSources().find(
                (source: MapBaseSource<Layer>) => source.sourceId === feature.source,
            );

            if (!source) {
                continue;
            }

            const selectedEntityId = feature.properties['uuid'];

            if (bySourceId[source.sourceId].includes(selectedEntityId)) {
                bySourceId[source.sourceId] = bySourceId[source.sourceId].filter(
                    (entityUuid) => entityUuid !== selectedEntityId,
                );

                delete this.selectedGeometries[selectedEntityId];
                continue;
            }

            try {
                const selectedEntity = await this.getLayerEntity(source.sourceId, selectedEntityId);
                bySourceId[source.sourceId].push(selectedEntityId);
                this.selectedGeometries[selectedEntityId] = selectedEntity.geom as SelectedGeometry;
            } catch {}
        }

        this.selectedEntityUuidsBySourceId.set(bySourceId);

        this.geographicFilterService.selectedExtentLayersAndEntities.set({
            entities: Object.values(bySourceId).flat(),
            layers: Object.keys(bySourceId),
        });
    }

    private setSelectedLayersWithoutEntities(): void {
        const bySourceId = this.selectedEntityUuidsBySourceId() ?? {};

        const layerIds = Object.keys(bySourceId);

        this.geographicFilterService.selectedExtentLayersAndEntities.set({
            entities: [],
            layers: layerIds,
        });
    }

    private async setSelectedSources(layerIds: string[]): Promise<void> {
        const selectedSources = [];
        const createFieldInterventionActiveLayerIds = this.createFieldInterventionService
            .activeLayers()
            .map((activeLayer: Layer) => activeLayer.id);

        const entitiesActiveLayerIds = this.entitiesTableByLayerService
            .activeLayers()
            .map((activeLayer: LayerWithAttributes) => activeLayer.id);

        const filteredLayerIds = layerIds.filter(
            (layerId: string) =>
                !createFieldInterventionActiveLayerIds.includes(layerId) || !entitiesActiveLayerIds.includes(layerId),
        );

        for (const layerId of filteredLayerIds) {
            const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId }));

            const style = await this.sourcesService.getLayerStyle(layer.layerStyle)!;
            const sourceWithoutZoom = this.mapLayerService.removeAllZoom([
                new MapLayerSource(GeomLayer.fromLayer(layer), style),
            ])[0] as MapLayerSource;

            selectedSources.push(sourceWithoutZoom);

            if (this.shouldDrawTableLayer()) {
                try {
                    await this.mapLayerService.drawTableLayer(
                        sourceWithoutZoom.sourceId,
                        sourceWithoutZoom.style,
                        true,
                        true,
                        sourceWithoutZoom.data as Layer,
                        {
                            fitBounds: false,
                        },
                        true,
                    );
                    this.sourcesService.sources.set([...this.sourcesService.sources(), sourceWithoutZoom]);
                } catch {
                    this.snackBarService.openWarningSnackBar('noPolygonTypeDataToShow');
                    break;
                }
            }
        }

        this.selectedSources.set(selectedSources);
    }
}
